/**
 * Rendered Mermaid SVG, kept across MermaidBlock mounts.
 *
 * A chat answer remounts its diagrams when a streamed block freezes into a
 * StableBlock and again when the stream completes and the answer renders as
 * one document (#3485). Without this cache each remount starts empty and
 * re-runs the async `mermaid.render`, so the diagram flickers.
 *
 * Only MermaidBlock's `mermaid.render` output is stored, rendered through
 * `renderMermaidExclusive`, so another caller's font or theme cannot end up in
 * a cached SVG. Mermaid scopes the
 * SVG's CSS and marker references to the element id it rendered with, so a
 * reused SVG gets that id rewritten to the new mount's id: two copies of one
 * diagram on a page must not share DOM ids.
 */

const MAX_ENTRIES = 50
// Large flowcharts render to hundreds of KB; bound the total, not just the count.
const MAX_TOTAL_CHARS = 8_000_000

type CachedSvg = { svg: string; renderId: string }
type DiagramKey = { code: string; theme: string }

const cache = new Map<string, CachedSvg>()
let totalChars = 0

function cacheKey({ code, theme }: DiagramKey): string {
  return `${theme}\u0000${code.trim()}`
}

function deleteEntry(key: string): void {
  const entry = cache.get(key)
  if (!entry) return
  totalChars -= entry.svg.length
  cache.delete(key)
}

export function getCachedMermaidSvg(code: string, theme: string, renderId: string): string | null {
  const key = cacheKey({ code, theme })
  const entry = cache.get(key)
  if (!entry) return null
  // Re-insert to mark as most recently used.
  cache.delete(key)
  cache.set(key, entry)
  return entry.svg.replaceAll(entry.renderId, renderId)
}

/**
 * Stores a render. `replaces` names the entry the same block cached before in
 * the same theme (e.g. a partial diagram while streaming), so a block holds
 * one entry per theme and a long streamed diagram cannot evict the other
 * diagrams on the page. Entries left by a theme switch age out via the LRU.
 */
export function setCachedMermaidSvg({
  code,
  theme,
  svg,
  renderId,
  replaces,
}: CachedSvg & DiagramKey & { replaces?: DiagramKey | null }): void {
  const key = cacheKey({ code, theme })
  if (replaces) deleteEntry(cacheKey(replaces))
  deleteEntry(key)
  if (svg.length > MAX_TOTAL_CHARS) return
  cache.set(key, { svg, renderId })
  totalChars += svg.length
  while (cache.size > MAX_ENTRIES || totalChars > MAX_TOTAL_CHARS) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    deleteEntry(oldest)
  }
}

// Test seam: resets the module-level cache between tests.
export function clearMermaidSvgCache(): void {
  cache.clear()
  totalChars = 0
}
